/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import {
  getMouseCoordinate,
  getValidValue,
  pluck,
  pluckNumber,
  preDefStr,
  MOUSEOUT,
  MOUSEMOVE,
  ZEROSTRING,
  extend2,
  BLANKSTRING,
  _manageInteractiveSpace
} from '@fusioncharts/core/src/lib';
import safeMax from '@fusioncharts/utils/src/array/safe-max';
import safeMin from '@fusioncharts/utils/src/array/safe-min';
import { priorityList } from '@fusioncharts/core/src/schedular';
import MSColumn2D from '@fusioncharts/charts/src/chart/mscolumn2d';
import _getData from '@fusioncharts/core/src/_internal/misc/fetch-data';
import { getDepsByType } from '@fusioncharts/core/src/dependency-manager';
import { submitData } from './editable-charts';

let UNDEF,
  transcoders = getDepsByType('transcoder'),
  UNDERSCORE = preDefStr.UNDERSCORE,
  BLANK = BLANKSTRING,
  count = 0,
  updateDataLimit = function (e) {
    let iapi = e.sender.apiInstance,
      canvas = iapi.getChildren('canvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      vCanvas = canvas.getChildren('vCanvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      iapiConf = iapi.config,
      dataLimit,
      data = e.data,
      endValue = data && data.endValue,
      startValue = data && data.startValue;
    if (endValue > iapiConf.yMax || endValue < iapiConf.yMin || startValue === iapiConf.yMin || startValue === iapiConf.yMax) {
      dataLimit = vCanvas.getDataLimits();
      iapiConf.yMax = dataLimit.max;
      iapiConf.yMin = dataLimit.min;
    }
  },
  /**
   * sanitise the format given by the user
   * @param {string} str value to be sanitised
   * @return {string} a string in lower case
   */
  sanitiseFormatStr = str => str.toString().toLowerCase();

/**
 * API class of dragcolumn inheriting from column class
 */
class DragBase extends MSColumn2D {
  /**
   * constructor to define the ei methods
   */
  constructor () {
    super();
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.getDataWithId = function () {
      var chartInstance = this,
        dataObj = chartInstance.apiInstance && chartInstance.apiInstance.getJSONData(),
        returnObj = [
          [BLANK]
        ],
        datasets = dataObj.dataset,
        catArr = (dataObj.categories && dataObj.categories[0] &&
                dataObj.categories[0].category),
        i = (datasets && datasets.length) || 0,
        vLinePassed = 0,
        setArr,
        catName,
        catObj,
        set,
        DS,
        item,
        dsID,
        id,
        j,
        ln;

      while (i--) {
        DS = datasets[i];
        if (DS) {
          returnObj[0][i + 1] = DS.id || DS.seriesname;
          dsID = DS.id || (i + 1);
          set = DS.data;
          ln = (set && set.length) || 0;
          for (j = 0; j < ln; j += 1) {
            item = j + 1;
            if (!returnObj[item]) {
              catObj = (catArr && catArr[j + vLinePassed]) || {};
              while (catObj.vline) {
                vLinePassed += 1;
                catObj = catArr[j + vLinePassed] || {};
              }
              catName = catObj.label || catObj.name || BLANK;
              returnObj[item] = [catName];
            }
            setArr = returnObj[item];
            id = set[j].id || (item + UNDERSCORE + dsID);
            setArr[i + 1] = [id, Number(set[j].value)];
          }
        }
      }

      return returnObj;
    };
    /**
     * Data of the chart in format user wants or in a 2d array
     * @param {string} [format] in which the data will be returned
     * @return {string | Object} chart data
     */
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.getData = function (format) {
      var chartInstance = this,
        jsonData = chartInstance.apiInstance && chartInstance.apiInstance.getJSONData(),
        returnData,
        datasets = jsonData.dataset,
        len = (datasets && datasets.length) || 0, // eslint-disable-line good-practices/no-single-usage-variable
        i = 0,
        transcoderObjSource,
        sanitisedFormat;

      // When a format is provided
      if (format) {
        sanitisedFormat = sanitiseFormatStr(format);
        // no transcoding needed for json
        if (/^json$/ig.test(sanitisedFormat)) {
          returnData = jsonData;
        } else {
          transcoderObjSource = transcoders[sanitisedFormat]();
          returnData = transcoderObjSource.fromJSON(jsonData, chartInstance).data;
        }
      } else {
        returnData = _getData.call(chartInstance.apiInstance);
        returnData.unshift([BLANK]);
        while (i < len) {
          returnData[0][i + 1] = datasets[i++].seriesname;
        }
      }
      return returnData;
    };
    /**
     * Function to set the upper limit of the chart
     * @param {number} limit new upper limit for the chart
     * @param {Function} callback is the function which gets the return value in its paramter if
     * @return {number} value of the returned function
     */
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.setUpperLimit = function (limit, callback) {
      var iapi = this.apiInstance,
        output;

      if (callback) {
        iapi.addJob(`setUpperLimitId${count++}`, function () {
          output = iapi.changeUpperLimits(limit); // eslint-disable-line good-practices/no-single-usage-variable
          if (typeof callback === 'function') {
            callback(output);
          }
        }, priorityList.postRender);
      } else {
        return iapi.changeUpperLimits(limit);
      }
    };
    /**
     * Function to set the lower limit of the chart
     * @param {number} limit new lower limit for the chart
     * @param {Function} callback is the function which gets the return value in its paramter if
     * @return {number} value of the returned function
     */
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.setLowerLimit = function (limit, callback) {
      var iapi = this.apiInstance,
        output;

      if (callback) {
        iapi.addJob(`setLowerLimitId${count++}`, function () {
          output = iapi.changeLowerLimits(limit); // eslint-disable-line good-practices/no-single-usage-variable
          if (typeof callback === 'function') {
            callback(output);
          }
        }, priorityList.postRender);
      } else {
        return iapi.changeLowerLimits(limit);
      }
    };
    /**
     * Function to get the lower limit of the chart
     * @param {Function} callback is the function which gets the return value in its paramter if
     * @return {number} lower limit value
     */
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.getLowerLimit = function (callback) {
      var iapi = this.apiInstance,
        yAxis = iapi.getChildren('yAxis')[0];

      if (yAxis) {
        if (callback) {
          iapi.addJob(`getLowerLimitId${count++}`, function () {
            if (typeof callback === 'function') {
              callback(yAxis.config.axisRange.min);
            }
          }, priorityList.postRender);
        } else {
          return yAxis.config.axisRange.min;
        }
      }
    };
    /**
     * Function to get the upper limit of the chart
     * @param {Function} callback is the function which gets the return value in its paramter if
     * @return {number} upper limit value
     */
    // eslint-disable-next-line good-practices/no-function-dependency
    this.eiMethods.getUpperLimit = function (callback) {
      var iapi = this.apiInstance,
        yAxis = iapi.getChildren('yAxis')[0];

      if (yAxis) {
        if (callback) {
          iapi.addJob(`getUpperLimitId${count++}`, function () {
            if (typeof callback === 'function') {
              callback(yAxis.config.axisRange.max);
            }
          }, priorityList.postRender);
        } else {
          return yAxis.config.axisRange.max;
        }
      }
    };
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'DragBase';
  }

  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    this.getName() !== 'DragNode' &&
      this.getFromEnv('chartInstance').addEventListener('dataplotdragend', updateDataLimit);
  }

  mouseoutHandler (e, _lastDatasetIndex, _lastPointIndex) {
    let chart = this,
      datasets = chart.config.datasetOrder || chart.getDatasets(),
      mouseTracker = chart.getChildren('mouseTracker')[0];
    // check if the data plot exists. else hide all existing tooltips.
    if (datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex].components.data[_lastPointIndex]) {
      datasets[_lastDatasetIndex]._firePlotEvent(MOUSEOUT, _lastPointIndex, e);
    } else {
      chart.getFromEnv('toolTipController').hideAll();
    }
    // delete stored last ds details
    delete mouseTracker._lastDatasetIndex;
    delete mouseTracker._lastPointIndex;
  }

  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'DragBase';
  }

  /**
   * Function which determines on which particular data of a dataset the mouse pointer is hovered
   * @param {Object} e A wrapper over the original event
   * @param  {Object} data The Data Object
   */
  _mouseEvtHandler (e, data) {
    var chart = this,
      mouseTracker = data.mouseTracker,
      datasets = chart.config.datasetOrder || chart.getDatasets(),
      coordinate = getMouseCoordinate(chart.getFromEnv('chart-container'), e.originalEvent, chart),
      dataset,
      hoveredInfo,
      pointFound = false,
      i = datasets.length,
      j,
      l,
      derivedEvensInfo,
      _lastDatasetIndex = mouseTracker._lastDatasetIndex,
      _lastPointIndex = mouseTracker._lastPointIndex,
      dragStart,
      eventType;

    (_lastPointIndex !== UNDEF) && datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex].components.data[_lastPointIndex] &&
        (dragStart = datasets[_lastDatasetIndex].components.data[_lastPointIndex].config.dragStart);
    if (!dragStart) {
    // @todo we have to implement this for charts with more than one canvas like candle stick
      while (i-- && !pointFound) {
        dataset = datasets[i];
        if (dataset && dataset.getState('visible')) {
          hoveredInfo = dataset._getHoveredPlot && dataset._getHoveredPlot(coordinate.chartX, coordinate.chartY);
          if (hoveredInfo && hoveredInfo.hovered) {
            pointFound = true;
            hoveredInfo.datasetIndex = i;
            derivedEvensInfo = mouseTracker.getMouseEvents(e, hoveredInfo.datasetIndex,
              hoveredInfo.pointIndex);
          }
        }
      }
    }

    // for drag event
    if (dragStart && _lastDatasetIndex !== UNDEF) {
      eventType = (e.type === MOUSEOUT) ? MOUSEMOVE : e.type;
      datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex]._firePlotEvent &&
              datasets[_lastDatasetIndex]._firePlotEvent(eventType, _lastPointIndex, e);
    }
    // @todo instead of sending event names, create a event object of that type and send it
    // fire out on last hovered plot
    if (!dragStart && (!pointFound || (derivedEvensInfo && derivedEvensInfo.fireOut)) &&
                _lastDatasetIndex !== UNDEF) {
      if (datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex]._firePlotEvent) {
        // when mouseout is fired and there aren't any events that need to be fired over the current plot,
        // call mouseouthamndler through settimeout.
        if (derivedEvensInfo && !derivedEvensInfo.events.length) {
          mouseTracker.mouseoutTimer = setTimeout(function () {
            chart.mouseoutHandler(e, _lastDatasetIndex, _lastPointIndex);
          }, 20);
        } else {
          chart.mouseoutHandler(e, _lastDatasetIndex, _lastPointIndex);
          clearTimeout(mouseTracker.mouseoutTimer);
        }
      }
    }
    // fire remaining events
    if (pointFound) {
      l = derivedEvensInfo.events && derivedEvensInfo.events.length;
      // Update only when events for current plot are present.
      // Reason: When cursor is present in territory of line/area plot but not directly over it, then
      // the fireOut of current plot was happening instead of the actual last plot.
      if (l) {
      // store the index of the hovered DS and plot
        mouseTracker._lastDatasetIndex = hoveredInfo.datasetIndex;
        _lastPointIndex = mouseTracker._lastPointIndex = hoveredInfo.pointIndex;
      }
      for (j = 0; j < l; j += 1) {
        dataset && dataset._firePlotEvent && dataset._firePlotEvent(derivedEvensInfo.events[j],
          _lastPointIndex, e);
      }
    }
  }

  /**
   * Configure function calls the parent configure function and adds eventListener to needed children
   * @param {Object} dataObj contains json data of the chart
   */
  parseChartAttr (dataObj) {
    super.parseChartAttr(dataObj);
    var chart = this,
      chartAttr = chart.getFromEnv('dataSource').chart,
      chartConfig;
    chartConfig = chart.config;
    chartConfig.formAction = getValidValue(chartAttr.formaction);

    if (chartAttr.submitdataasxml === ZEROSTRING && !chartAttr.formdataformat) {
      chartAttr.formdataformat = transcoders.csv().format;
    }

    chartConfig.formDataFormat = pluck(chartAttr.formdataformat,
      transcoders.xml().format);
    chartConfig.formTarget = pluck(chartAttr.formtarget, '_self');
    chartConfig.formMethod = pluck(chartAttr.formmethod, 'POST');
    chartConfig.submitFormAsAjax = pluckNumber(chartAttr.submitformusingajax, 1);
    chartConfig.restoreBtnTitle = pluck(
      chartAttr.restoretext,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE restorebtntitle parsing after two releases from 3.13.0
      chartAttr.restorebtntitle,
      'Restore'
    );
    chartConfig.submitBtnTitle = pluck(
      chartAttr.submittext,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE formbtntitle parsing after two releases from 3.13.0
      chartAttr.formbtntitle,
      'Submit'
    );

    // Form Button
    chartConfig.showFormBtn = pluckNumber(
      chartAttr.enablesubmit,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE showformbtn parsing after two releases from 3.13.0
      chartAttr.showformbtn,
      1
    ) && chartConfig.formAction;
    chartConfig.showRestoreBtn = pluckNumber(
      chartAttr.enablerestore,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE showrestorebtn parsing after two releases from 3.13.0
      chartAttr.showrestorebtn,
      1
    );
    chartConfig.formBtnTitle = pluck(chartAttr.formbtntitle, 'Submit');
    chartConfig.formBtnStyle = {
      fontSize: chartConfig.style.outCanfontSize,
      fontFamily: chartConfig.style.outCanfontFamily,
      fontWeight: 'bold'
    };
    chartConfig.restoreBtnWidth = pluckNumber(chartAttr.restorebtnwidth, 0);
    chartConfig.allowAxisChange = pluckNumber(chartAttr.allowaxischange, 1);
    if (chartAttr.toolbary || chartAttr.toolbarx) {
      chartConfig.spaceHardCoded = true;
    } else {
      delete chartConfig.spaceHardCoded;
    }
    chartConfig.drawTrendRegion = pluckNumber(chartAttr.drawcrossline, 0);
  }

  /**
   * Function to store the initial axis value
   * @param {Number} min is minimum data limit
   * @param {Number} max is maximum data limit
   */
  _storeIntialLimit (min, max) {
    this.config.axisInitialLimit = { min, max };
  }

  /**
   * Over-writing mscartesian createToolBoxMenu function
   */
  attachMenuButtons () {
    super.attachMenuButtons();
    this.addConfigureOptions();
  }
  /**
   * Function for adding menu otions to be shown in the drop-down menu
   */
  addConfigureOptions () {
    var chart = this,
      toolbar = chart.getFromEnv('toolbar'),
      hamburgerMenu = toolbar.getChild(`hamburgerMenu-${toolbar.getId()}-${chart.getId()}-0`), // eslint-disable-line good-practices/no-single-usage-variable
      chartConf = chart.config,
      restore,
      submit,
      configureTools = [
        {
          name: 'Increase Upper Limit',
          handler: function () {
            var axisRange = chart.getChildren('yAxis')[0].getLimit();
            chart.changeUpperLimits(axisRange.max + axisRange.tickInterval);
          },
          action: 'click'
        },
        {
          name: 'Increase Lower Limit',
          handler: function () {
            var axisRange = chart.getChildren('yAxis')[0].getLimit();
            chart.changeLowerLimits(axisRange.min + axisRange.tickInterval);
          },
          action: 'click'
        },
        {
          name: 'Decrease Upper Limit',
          handler: function () {
            var axisRange = chart.getChildren('yAxis')[0].getLimit();
            chart.changeUpperLimits(axisRange.max - axisRange.tickInterval);
          },
          action: 'click'
        },
        {
          name: 'Decrease Lower Limit',
          handler: function () {
            var axisRange = chart.getChildren('yAxis')[0].getLimit();
            chart.changeLowerLimits(axisRange.min - axisRange.tickInterval);
          },
          action: 'click'
        }
      ];

    if (chartConf.showFormBtn) {
      submit = {
        name: chartConf.submitBtnTitle,
        handler: function () {
          submitData.call(chart);
        },
        action: 'click'
      };
      configureTools.push(submit);
    }

    if (chartConf.showRestoreBtn) {
      restore = {
        name: chartConf.restoreBtnTitle,
        handler: function () {
          chart.restoreData();
        },
        action: 'click'
      };
      configureTools.push(restore);
    }

    if (chartConf.allowAxisChange) {
      hamburgerMenu.appendInMenu(configureTools);
    }
  }

  /**
   * Function to restore all the data to the original json Data
   */
  restoreData () {
    var chart = this,
      yAxis = chart.getChildren('yAxis')[0],
      chartAttr = chart.getFromEnv('chart-attrib'),
      axisInitialLimit = chart.config.axisInitialLimit;
    // eslint-disable-next-line good-practices/no-function-dependency
    chart.iterateComponents((child) => {
      let data;
      // eslint-disable-next-line good-practices/no-single-usage-variable
      if ((data = child.getChildren('dataset'))) {
        data.forEach(function (dataset) {
          dataset.restore();
        });
      }
    });
    yAxis.resetStoredLimits();
    yAxis.setAxisConfig({
      axisMaxValue: chartAttr.yaxismaxvalue,
      axisMinValue: chartAttr.yaxisminvalue
    });
    yAxis.setDataLimit(axisInitialLimit.max, axisInitialLimit.min);
    chart.fireChartInstanceEvent('dataRestored', {});
    chart._manageInteractiveSpace();
  }

  /**
   * Function to change the lowerLimit of the axis using the drop down menu item
   * @param {number} lowerLimit The limit to which lower limit of the axis has to be extended
   * @return {boolean} if axis limit is changed
   */
  changeLowerLimits (lowerLimit) {
    var chart = this,
      yAxis = chart.getChildren('yAxis')[0],
      axisRange = yAxis.getLimit(),
      min = axisRange.min,
      config = chart.config,
      minValue = safeMin([config.yMin, yAxis.getTrendLineLimits()[0]]), // eslint-disable-line good-practices/no-single-usage-variable
      limitchanged = false,
      max = axisRange.max;
    chart.getFromEnv('animationManager').setAnimationState('update');
    if ((lowerLimit !== UNDEF) && lowerLimit < minValue && lowerLimit !== axisRange.min && config.allowAxisChange) {
      min = lowerLimit;
      limitchanged = true;
    }

    if (limitchanged) {
      yAxis.resetStoredLimits();
      yAxis.setAxisConfig({
        axisMaxValue: max,
        axisMinValue: min,
        showLowerLimit: true
      });
      yAxis.setDataLimit(max, min);
      chart._manageInteractiveSpace();
    }
    return limitchanged;
  }

  /**
   * Function to change the upperLimit of the axis using the drop down menu item
   * @param {number} upperLimit The limit to which upper limit of the axis has to be extended
   * @return {boolean} if axis limit is changed
   */
  changeUpperLimits (upperLimit) {
    var chart = this,
      // components = chart.components,
      yAxis = chart.getChildren('yAxis')[0],
      axisRange = yAxis.getLimit(),
      min = axisRange.min,
      config = chart.config,
      maxValue = safeMax([config.yMax, yAxis.getTrendLineLimits()[1]]), // eslint-disable-line good-practices/no-single-usage-variable
      limitchanged = false,
      max;

    chart.getFromEnv('animationManager').setAnimationState('update');

    if ((upperLimit !== UNDEF) && upperLimit > maxValue && upperLimit !== axisRange.max && config.allowAxisChange) {
      max = upperLimit;
      limitchanged = true;
    }

    if (limitchanged) {
      yAxis.resetStoredLimits();
      yAxis.setAxisConfig({
        axisMaxValue: max,
        axisMinValue: min,
        showUpperLimit: true
      });
      yAxis.setDataLimit(max, min);
      chart._manageInteractiveSpace();
    }
    return limitchanged;
  }

  /**
   * Function to get the jsonData
   * @return {Object} the jsonData
   */
  getJSONData () {
    var chart = this,
      canvas = chart.getChildren('canvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      vCanvas = canvas.getChildren('vCanvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      rawObj = chart.getFromEnv('dataSource'), // eslint-disable-line good-practices/no-single-usage-variable
      groupManager,
      datasetsArr = [],
      dataset,
      datasets,
      dataObj,
      i,
      len,
      jsonObj;

    vCanvas.iterateComponents(component => {
      if (component.getType() === 'group') {
        groupManager = component;
      }
    });

    if (groupManager) {
      datasetsArr = groupManager.getJSONData && groupManager.getJSONData();
    } else {
      datasets = chart.getDatasets();
      len = datasets.length;
      for (i = 0; i < len; i++) {
        dataset = datasets[i];
        dataObj = {
          'seriesname': dataset.config.seriesname,
          'data': dataset.getJSONData().data
        };
        datasetsArr.push(dataObj);
      }
    }

    jsonObj = extend2({}, rawObj);
    jsonObj.dataset = datasetsArr;
    return jsonObj;
  }
}
DragBase.prototype._manageInteractiveSpace = _manageInteractiveSpace;
export default DragBase;
